
import React from 'react';
import { Checkbox } from "@/components/ui/checkbox";
import { Profession } from '@/lib/professionTypes';
import { Badge } from "@/components/ui/badge";
import { X } from 'lucide-react';
import { ScrollArea } from "@/components/ui/scroll-area";

interface ProfessionsListProps {
  isLoading: boolean;
  filteredProfessions: Profession[];
  groupedProfessions: Record<string, Profession[]>;
  selectedProfessions: Record<string, boolean>;
  onProfessionClick: (id: string) => void;
}

const ProfessionsList = ({ 
  isLoading, 
  filteredProfessions, 
  groupedProfessions, 
  selectedProfessions, 
  onProfessionClick 
}: ProfessionsListProps) => {
  // Kiválasztott szakmák száma
  const selectedCount = Object.values(selectedProfessions).filter(Boolean).length;
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  
  if (filteredProfessions.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-muted-foreground">
        Nincs találat a keresésre
      </div>
    );
  }
  
  // Kiválasztott szakmák azonosítóinak listája
  const selectedProfessionIds = Object.entries(selectedProfessions)
    .filter(([_, selected]) => selected)
    .map(([id]) => id);
  
  return (
    <ScrollArea className="h-full">
      <div className="space-y-4">
        {/* Kiválasztott szakmák megjelenítése fixed pozícióban */}
        {selectedCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-3 sticky top-0 bg-white pt-1 pb-2 z-10 border-b">
            {selectedProfessionIds.map(id => {
              // Megkeressük a szakma adatait az id alapján
              const profession = filteredProfessions.find(p => p.id === id);
              return (
                <Badge 
                  key={id} 
                  variant="secondary"
                  className="flex items-center gap-1 px-2 py-1"
                >
                  {profession?.name || id}
                  <button
                    onClick={() => onProfessionClick(id)}
                    className="ml-1 rounded-full hover:bg-gray-200 p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
        )}
        
        {/* Kategóriák és szakmák listája */}
        {Object.entries(groupedProfessions).map(([category, profs]) => (
          <div key={category} className="space-y-1">
            <h4 className="text-xs font-medium text-muted-foreground">{category}</h4>
            <div className="pl-2 space-y-1">
              {profs.map(profession => (
                <div key={profession.id} className="flex items-start space-x-2">
                  <Checkbox
                    id={`prof-${profession.id}`}
                    checked={selectedProfessions[profession.id] || false}
                    onCheckedChange={() => onProfessionClick(profession.id)}
                  />
                  <label
                    htmlFor={`prof-${profession.id}`}
                    className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {profession.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};

export default ProfessionsList;
